import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Typography,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Button,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

const styles = theme => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  sequenceInfo: {
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    marginBottom: theme.spacing(3),
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  filterControl: {
    minWidth: 200,
  },
  table: {
    minWidth: 650,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  statusChip: {
    minWidth: 80,
  },
  activeStatus: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  pausedStatus: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  completedStatus: {
    backgroundColor: '#2196f3',
    color: 'white',
  },
  cancelledStatus: {
    backgroundColor: '#9e9e9e',
    color: 'white',
  },
  progress: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  progressBar: {
    width: 100,
  },
});

const EnrollmentManager = ({
  classes,
  sequence,
  enrollments,
  onPause,
  onResume,
  onRemove,
}) => {
  const history = useHistory();
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredEnrollments = enrollments.filter(enrollment => {
    if (statusFilter === 'all') return true;
    return enrollment.status === statusFilter;
  });

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'active':
        return classes.activeStatus;
      case 'paused':
        return classes.pausedStatus;
      case 'completed':
        return classes.completedStatus;
      case 'cancelled':
        return classes.cancelledStatus;
      default:
        return '';
    }
  };

  const getStatusLabel = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const calculateProgress = (enrollment) => {
    if (!sequence?.emailSequenceSteps || sequence.emailSequenceSteps.length === 0) {
      return 0;
    }
    return Math.round((enrollment.currentStep / sequence.emailSequenceSteps.length) * 100);
  };

  const handlePause = (enrollmentId) => {
    if (window.confirm('Are you sure you want to pause this enrollment? The user will not receive any more emails from this sequence until resumed.')) {
      if (onPause) {
        onPause(enrollmentId);
      }
    }
  };

  const handleResume = (enrollmentId) => {
    if (onResume) {
      onResume(enrollmentId);
    }
  };

  const handleRemove = (enrollmentId, userName) => {
    if (window.confirm(`Are you sure you want to remove ${userName} from this sequence? This action cannot be undone.`)) {
      if (onRemove) {
        onRemove(enrollmentId);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (e) {
      return dateString;
    }
  };

  const getStepLabel = (currentStep, totalSteps) => {
    if (currentStep > totalSteps) return `Completed`;
    return `Step ${currentStep} of ${totalSteps}`;
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/admin/email_sequences')}
            style={{ marginBottom: 8 }}
          >
            Back to Sequences
          </Button>
          <Typography variant="h4">Sequence Enrollments</Typography>
        </div>
      </div>

      {sequence && (
        <Paper className={classes.sequenceInfo}>
          <Typography variant="h6">{sequence.name}</Typography>
          {sequence.description && (
            <Typography variant="body2" color="textSecondary">
              {sequence.description}
            </Typography>
          )}
          <Box mt={1}>
            <Chip
              label={`${sequence.emailSequenceSteps?.length || 0} steps`}
              size="small"
              style={{ marginRight: 8 }}
            />
            <Chip
              label={sequence.active ? 'Active' : 'Inactive'}
              size="small"
              className={sequence.active ? classes.activeStatus : classes.pausedStatus}
            />
          </Box>
        </Paper>
      )}

      <div className={classes.filters}>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="paused">Paused</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </div>

      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="center">Progress</TableCell>
              <TableCell align="center">Current Step</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell>Enrolled Date</TableCell>
              <TableCell>Completed Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEnrollments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {enrollments.length === 0
                      ? 'No users enrolled in this sequence yet.'
                      : 'No enrollments match your filter.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredEnrollments.map(enrollment => (
                <TableRow key={enrollment.id}>
                  <TableCell>
                    <Typography variant="body1">
                      {enrollment.user?.name || 'Unknown User'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {enrollment.user?.email || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <div className={classes.progress}>
                      <LinearProgress
                        variant="determinate"
                        value={calculateProgress(enrollment)}
                        className={classes.progressBar}
                      />
                      <Typography variant="caption">
                        {calculateProgress(enrollment)}%
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {getStepLabel(
                        enrollment.currentStep,
                        sequence?.emailSequenceSteps?.length || 0
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={getStatusLabel(enrollment.status)}
                      size="small"
                      className={`${classes.statusChip} ${getStatusChipClass(enrollment.status)}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(enrollment.enrolledAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(enrollment.completedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <div className={classes.actions}>
                      {enrollment.status === 'active' && (
                        <Tooltip title="Pause">
                          <IconButton
                            size="small"
                            onClick={() => handlePause(enrollment.id)}
                          >
                            <PauseIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {enrollment.status === 'paused' && (
                        <Tooltip title="Resume">
                          <IconButton
                            size="small"
                            onClick={() => handleResume(enrollment.id)}
                            color="primary"
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(enrollment.status === 'active' || enrollment.status === 'paused') && (
                        <Tooltip title="Remove from Sequence">
                          <IconButton
                            size="small"
                            onClick={() => handleRemove(enrollment.id, enrollment.user?.name)}
                            color="secondary"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

EnrollmentManager.propTypes = {
  classes: PropTypes.object.isRequired,
  sequence: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    active: PropTypes.bool,
    emailSequenceSteps: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        position: PropTypes.number,
      })
    ),
  }),
  enrollments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      currentStep: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
      enrolledAt: PropTypes.string,
      completedAt: PropTypes.string,
      user: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        email: PropTypes.string,
      }),
    })
  ).isRequired,
  onPause: PropTypes.func,
  onResume: PropTypes.func,
  onRemove: PropTypes.func,
};

EnrollmentManager.defaultProps = {
  enrollments: [],
};

export default withStyles(styles)(EnrollmentManager);

